import { MaintenanceIntervalType } from 'services/maintenance-tracking/types';
import { MaintenanceHistory } from 'types/MaintenanceHistory';
import { Action, MaintenanceInterval } from 'types/MaintenanceInterval';

export default class MaintenanceNextIntervalsFactory {
  private everyHoursIntervals: MaintenanceInterval[];
  private firstHoursIntervals: MaintenanceInterval[];
  private hoursMaintenanceHistoryValues: number[];

  constructor(
    private readonly engineHours: number,
    private readonly intervals: MaintenanceInterval[],
    private readonly history: MaintenanceHistory[] = [],
  ) {
    this.everyHoursIntervals = this.getEveryHoursIntervals();
    this.firstHoursIntervals = this.getFirstHoursIntervals();
    this.hoursMaintenanceHistoryValues =
      this.getHoursMaintenanceHistoryValues();
  }

  getIntervals() {
    const nextIntervals: MaintenanceInterval[] = [];
    const combinedIntervals = [
      ...this.everyHoursIntervals,
      ...this.firstHoursIntervals,
    ];

    const minimumInterval = combinedIntervals[0]?.intervalValue ?? 1;

    let isInMinimumInterval = false;

    const nextInterval = (() => {
      const sortedHistory = [...this.history].sort(
        (a, b) => a.completedEngineHours - b.completedEngineHours,
      );

      const lastMaintenanceEngineHours =
        sortedHistory.at(-1)?.completedEngineHours ?? 0;

      const maximumEngineHours = Math.max(
        lastMaintenanceEngineHours,
        this.engineHours,
      );
      const intervalDifference = maximumEngineHours % minimumInterval;
      const userEngineHours = Math.floor(maximumEngineHours);

      if (userEngineHours <= minimumInterval) {
        isInMinimumInterval = true;
        return userEngineHours;
      }

      let lastInterval = Math.floor(maximumEngineHours - intervalDifference);

      if (
        this.engineHours > lastMaintenanceEngineHours &&
        intervalDifference === 0
      ) {
        lastInterval -= minimumInterval;
      }

      const hasCompletedInterval =
        (sortedHistory.at(-1)?.completedEngineHours ?? 0) >= lastInterval;

      isInMinimumInterval = false;
      return hasCompletedInterval
        ? lastInterval + minimumInterval
        : lastInterval;
    })();

    let interval = nextInterval;

    const initialIntervalsBeforeRepeat = this.firstHoursIntervals.filter(
      i => i.intervalValue < minimumInterval,
    );

    if (
      interval <= minimumInterval &&
      isInMinimumInterval &&
      initialIntervalsBeforeRepeat.length > 0 &&
      !this.checkSingleIntervalInMaintenanceHistory(
        initialIntervalsBeforeRepeat.at(-1)?.intervalValue ?? minimumInterval,
      )
    ) {
      nextIntervals.push(
        ...this.getInitialIntervals(interval, [
          ...this.firstHoursIntervals,
          ...this.everyHoursIntervals,
        ]),
      );
    } else {
      interval = interval <= minimumInterval ? minimumInterval : interval;

      for (let i = 0; i <= 2; i++) {
        const next = this.getNextInterval(
          nextIntervals,
          interval,
          minimumInterval,
        );

        const filtered = this.getHourIntervals(next);

        if (filtered) {
          nextIntervals.push(filtered);
          interval += minimumInterval;
        }
      }
    }

    const nonHourBasedIntervals =
      this.intervals.filter(
        i =>
          i.intervalType !== MaintenanceIntervalType.EveryXHours &&
          i.intervalType !== MaintenanceIntervalType.XHours,
      ) ?? [];

    nextIntervals.push(...nonHourBasedIntervals);

    return nextIntervals;
  }

  private getEveryHoursIntervals() {
    return this.intervals.filter(
      schedule => schedule.intervalType === 'Every X Hours',
    );
  }

  private getInitialIntervals(
    currentInterval: number,
    allIntervals: MaintenanceInterval[],
  ): MaintenanceInterval[] {
    const uniqueIntervalsMap = new Map<number, MaintenanceInterval>();

    for (const interval of allIntervals) {
      uniqueIntervalsMap.set(interval.intervalValue, interval);
    }

    const sortedIntervals = Array.from(uniqueIntervalsMap.values()).sort(
      (a, b) => a.intervalValue - b.intervalValue,
    );

    let indexOfNextInterval = sortedIntervals.findIndex(
      i => i.intervalValue >= currentInterval,
    );

    if (indexOfNextInterval === -1) {
      indexOfNextInterval = 0;
    }

    let selectedIntervals: MaintenanceInterval[] = [];

    do {
      selectedIntervals = this.getIntervalRange(
        indexOfNextInterval,
        sortedIntervals,
      );
      if (selectedIntervals.length === 0) return [];

      const firstIntervalValue = selectedIntervals[0]?.intervalValue;
      if (
        firstIntervalValue !== undefined &&
        !this.checkSingleIntervalInMaintenanceHistory(firstIntervalValue)
      ) {
        break;
      }

      indexOfNextInterval += 1;
    } while (indexOfNextInterval < sortedIntervals.length);

    return selectedIntervals
      .map(interval => this.getHourIntervals(interval.intervalValue, true))
      .filter((i): i is MaintenanceInterval => !!i);
  }

  private getIntervalRange(
    index: number,
    sortedIntervals: MaintenanceInterval[],
  ) {
    if (sortedIntervals.length < 3) {
      return [];
    }

    if (index > 0) {
      const fromIndex = Math.max(0, index - 1);
      const toIndex = Math.min(sortedIntervals.length, index + 2);
      return sortedIntervals.slice(fromIndex, toIndex);
    } else {
      return sortedIntervals.slice(0, 3);
    }
  }

  private getNextInterval(
    intervals: MaintenanceInterval[],
    interval: number,
    minimumInterval: number,
  ): number {
    let nextInterval = interval;

    const historyValues = [
      ...this.hoursMaintenanceHistoryValues,
      ...intervals.map(i => i.intervalValue),
    ];
    const maxHistoryValue = Math.max(...historyValues) ?? 0;

    while (maxHistoryValue >= nextInterval) {
      nextInterval += minimumInterval;

      if (nextInterval > Number.MAX_SAFE_INTEGER - minimumInterval) {
        break;
      }
    }

    return nextInterval;
  }

  private getHourIntervals(
    value: number,
    isForInitialState = false,
  ): MaintenanceInterval | undefined {
    const firstIntervals = this.firstHoursIntervals.filter(
      i => i.intervalValue === value,
    );

    const everyIntervals = this.everyHoursIntervals.filter(
      i => value % i.intervalValue === 0,
    );

    const finalIntervals = isForInitialState
      ? [...firstIntervals, ...everyIntervals]
      : [...everyIntervals, ...firstIntervals];

    const actions: Action[] = finalIntervals.flatMap(i => i.actions);

    const maintenanceInterval = finalIntervals[0];
    if (!maintenanceInterval) return undefined;

    return {
      interval: `${value} Hours`,
      actions,
      sortOrderPrimary: maintenanceInterval.sortOrderPrimary,
      sortOrderSecondary: maintenanceInterval.sortOrderSecondary,
      intervalType: MaintenanceIntervalType.EveryXHours,
      intervalValue: value,
      displayIntervalType: `${value} Hours`,
    };
  }

  private getFirstHoursIntervals() {
    return this.intervals.filter(
      schedule => schedule.intervalType === 'X Hours',
    );
  }

  private getHoursMaintenanceHistoryValues() {
    return this.history
      .filter(
        history =>
          history.intervalType === 'X Hours' ||
          history.intervalType === 'Every X Hours',
      )
      .map(history => history.intervalValue);
  }

  private checkSingleIntervalInMaintenanceHistory(interval: number) {
    const lastHistoryValue = Math.max(...this.hoursMaintenanceHistoryValues);
    return lastHistoryValue ? lastHistoryValue >= interval : false;
  }
}
